import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Users, 
  UserPlus, 
  Search, 
  Filter, 
  Calendar,
  Award,
  Truck,
  Edit3,
  Trash2,
  UserCheck,
  Star,
  Shield,
  Settings,
  Building,
  Home,
  ClipboardList
} from 'lucide-react'
import { apiClient } from '../config/api'
import StaffForm from './StaffForm'
import StaffAvailability from './StaffAvailability'

// Fleet Staff Management - Complete staff management system
// Handles drivers, supervisors, loaders, coordinators, maintenance staff, security

// Types based on database schema
interface FleetStaff {
  id: number
  staff_code: string
  first_name: string
  last_name: string
  full_name: string
  staff_type: 'DRIVER' | 'LOADER' | 'SUPERVISOR' | 'COORDINATOR' | 'MAINTENANCE' | 'SECURITY' | 'EXTERNAL_PROVIDER'
  employment_status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED'
  email?: string
  phone?: string
  hire_date: Date
  department?: string
  position?: string
  supervisor_id?: number
  driver_license_class?: string
  driver_license_number?: string
  license_expiry_date?: Date
  performance_rating: number
  total_missions_completed: number
  shift_pattern: string
  max_hours_per_day?: number
  max_hours_per_week?: number
  hourly_rate?: number
  monthly_salary?: number
  supervisor_name?: string
  created_at: Date
}

interface StaffAvailability {
  id: number
  staff_id: number
  date: Date
  shift_start: string
  shift_end: string
  availability_status: 'AVAILABLE' | 'ASSIGNED' | 'ON_MISSION' | 'ON_BREAK' | 'SICK_LEAVE' | 'VACATION' | 'UNAVAILABLE'
  assigned_mission_id?: string
  work_location?: string
  notes?: string
}

interface StaffCertification {
  id: number
  staff_id: number
  certification_name: string
  certification_type: 'LICENSE' | 'SAFETY' | 'TECHNICAL' | 'COMPLIANCE'
  issue_date: Date
  expiry_date?: Date
  status: 'ACTIVE' | 'EXPIRED' | 'SUSPENDED' | 'REVOKED'
}

interface FleetStaffManagementProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const FleetStaffManagement: React.FC<FleetStaffManagementProps> = ({ showMessage }) => {
  const { t } = useTranslation()
  // State management
  const [staff, setStaff] = useState<FleetStaff[]>([])
  const [selectedStaff, setSelectedStaff] = useState<FleetStaff | null>(null)
  
  // UI State
  const [activeTab, setActiveTab] = useState<'directory' | 'availability'>('directory')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('ACTIVE')
  const [showStaffForm, setShowStaffForm] = useState(false)
  const [editingStaff, setEditingStaff] = useState<FleetStaff | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [staffStats, setStaffStats] = useState({
    active_drivers: 0,
    total_active_staff: 0,
    active_supervisors: 0,
    external_providers: 0,
    total_missions_completed: 0
  })

  // Load data on mount and when filters change (search is debounced)
  useEffect(() => {
    const timer = setTimeout(() => loadStaffData(), searchTerm ? 300 : 0)
    return () => clearTimeout(timer)
  }, [filterType, filterStatus, searchTerm])

  // Recalculate statistics when staff data changes
  useEffect(() => {
    loadStaffStats()
  }, [staff])

  // Load staff data from the real API (fleet_staff table)
  const loadStaffData = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const params: Record<string, string> = {}
      if (searchTerm.trim()) params.search = searchTerm.trim()
      if (filterType !== 'all') params.staff_type = filterType
      params.employment_status = filterStatus
      const { data } = await apiClient.get('/staff', params)
      const rows = Array.isArray(data) ? data : []
      const parsed: FleetStaff[] = rows.map((row: any) => ({
        ...row,
        hire_date: row.hire_date ? new Date(row.hire_date) : new Date(),
        license_expiry_date: row.license_expiry_date ? new Date(row.license_expiry_date) : undefined,
        created_at: row.created_at ? new Date(row.created_at) : new Date(),
        performance_rating: Number(row.performance_rating) || 0,
        total_missions_completed: Number(row.total_missions_completed) || 0
      }))
      setStaff(parsed)
    } catch (error: any) {
      console.error('Failed to load staff:', error)
      setStaff([])
      setLoadError(error?.message || 'Failed to load staff')
      if (showMessage) showMessage('Failed to load staff from server', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Recompute staff statistics from loaded data
  const loadStaffStats = async () => {
    const activeStaff = staff.filter(s => s.employment_status === 'ACTIVE')
    setStaffStats({
      active_drivers: activeStaff.filter(s => s.staff_type === 'DRIVER').length,
      total_active_staff: activeStaff.length,
      active_supervisors: activeStaff.filter(s => s.staff_type === 'SUPERVISOR').length,
      external_providers: activeStaff.filter(s => s.staff_type === 'EXTERNAL_PROVIDER').length,
      total_missions_completed: activeStaff.reduce((sum, s) => sum + s.total_missions_completed, 0)
    })
  }

  // Filter staff based on search and filters
  const filteredStaff = staff.filter(member => {
    const matchesSearch = member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.staff_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.department?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesType = filterType === 'all' || member.staff_type === filterType
    const matchesStatus = filterStatus === 'all' || member.employment_status === filterStatus
    
    return matchesSearch && matchesType && matchesStatus
  })

  // Get staff type icon
  const getStaffTypeIcon = (type: string) => {
    switch (type) {
      case 'DRIVER': return <Truck className="w-4 h-4 text-blue-600" />
      case 'SUPERVISOR': return <UserCheck className="w-4 h-4 text-purple-600" />
      case 'LOADER': return <Users className="w-4 h-4 text-green-600" />
      case 'COORDINATOR': return <Settings className="w-4 h-4 text-orange-600" />
      case 'MAINTENANCE': return <Settings className="w-4 h-4 text-red-600" />
      case 'SECURITY': return <Shield className="w-4 h-4 text-gray-600" />
      case 'EXTERNAL_PROVIDER': return <Building className="w-4 h-4 text-indigo-600" />
      default: return <Users className="w-4 h-4 text-gray-600" />
    }
  }

  // Get employment status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100'
      case 'INACTIVE': return 'text-gray-600 bg-gray-100'
      case 'ON_LEAVE': return 'text-yellow-600 bg-yellow-100'
      case 'TERMINATED': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }


  // Handle staff actions
  const handleCreateStaff = () => {
    setEditingStaff(null)
    setShowStaffForm(true)
  }

  const handleEditStaff = (staffMember: FleetStaff) => {
    setEditingStaff(staffMember)
    setShowStaffForm(true)
  }

  const handleSaveStaff = async (staffData: FleetStaff) => {
    const payload = {
      staff_code: staffData.staff_code,
      first_name: staffData.first_name,
      last_name: staffData.last_name,
      staff_type: staffData.staff_type,
      employment_status: staffData.employment_status,
      email: staffData.email || null,
      phone: staffData.phone || null,
      department: staffData.department || null,
      position: staffData.position || null,
      hire_date: staffData.hire_date ? staffData.hire_date.toISOString().split('T')[0] : null,
      supervisor_id: staffData.supervisor_id || null,
      driver_license_number: staffData.driver_license_number || null,
      driver_license_class: staffData.driver_license_class || null,
      license_expiry_date: staffData.license_expiry_date
        ? staffData.license_expiry_date.toISOString().split('T')[0]
        : null,
      shift_pattern: staffData.shift_pattern,
      max_hours_per_day: staffData.max_hours_per_day,
      max_hours_per_week: staffData.max_hours_per_week,
      hourly_rate: staffData.hourly_rate ?? null,
      monthly_salary: staffData.monthly_salary ?? null,
      customer_id: (staffData as any).customer_id ?? null
    }

    try {
      if (editingStaff) {
        await apiClient.put(`/staff/${editingStaff.id}`, payload)
      } else {
        await apiClient.post('/staff', payload)
      }
      await loadStaffData()
    } catch (error: any) {
      console.error('Failed to save staff member:', error)
      if (showMessage) {
        showMessage(error?.message || 'Failed to save staff member', 'error')
      }
      throw error
    }
  }

  const handleDeleteStaff = async (staffMember: FleetStaff) => {
    if (!window.confirm(`Terminate ${staffMember.full_name}? Their record is kept with status TERMINATED.`)) {
      return
    }
    try {
      await apiClient.delete(`/staff/${staffMember.id}`)
      if (showMessage) {
        showMessage(`Staff member ${staffMember.full_name} terminated`, 'success')
      }
      if (selectedStaff?.id === staffMember.id) {
        setSelectedStaff(null)
      }
      await loadStaffData()
    } catch (error: any) {
      console.error('Failed to terminate staff member:', error)
      if (showMessage) {
        showMessage(error?.message || 'Failed to terminate staff member', 'error')
      }
    }
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('staff_management')}</h1>
                <p className="text-gray-600">{t('staff_management_desc')}</p>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center space-x-6 ml-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{staffStats.active_drivers || staff.filter(s => s.staff_type === 'DRIVER').length}</div>
                <div className="text-xs text-gray-500">{t('drivers')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{staffStats.total_active_staff || staff.filter(s => s.employment_status === 'ACTIVE').length}</div>
                <div className="text-xs text-gray-500">{t('active')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{staffStats.active_supervisors || staff.filter(s => s.staff_type === 'SUPERVISOR').length}</div>
                <div className="text-xs text-gray-500">{t('supervisors')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{staffStats.external_providers || staff.filter(s => s.staff_type === 'EXTERNAL_PROVIDER').length}</div>
                <div className="text-xs text-gray-500">{t('prestateurs')}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleCreateStaff}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <UserPlus className="w-4 h-4" />
              <span>{t('add_staff')}</span>
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-900">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 px-6 flex-shrink-0">
        <div className="flex space-x-8">
          {[
            { id: 'directory', label: t('staff_directory'), icon: Users },
            { id: 'availability', label: t('availability'), icon: Calendar }
          ].map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Staff List */}
        <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
          {/* Search and Filters */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3 mb-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('search_staff')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg">
                <Filter className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('all_staff_types')}</option>
                <option value="DRIVER">{t('drivers')}</option>
                <option value="SUPERVISOR">{t('supervisors')}</option>
                <option value="LOADER">Loaders</option>
                <option value="COORDINATOR">Coordinators</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="SECURITY">Security</option>
                <option value="EXTERNAL_PROVIDER">External Providers</option>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('all_status')}</option>
                <option value="ACTIVE">{t('active')}</option>
                <option value="INACTIVE">{t('inactive')}</option>
                <option value="ON_LEAVE">{t('on_leave')}</option>
              </select>
            </div>
          </div>

          {/* Staff List */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading && (
              <div className="p-6 text-center text-sm text-gray-500">Loading staff…</div>
            )}
            {!loading && loadError && (
              <div className="p-6 text-center text-sm text-red-600">{loadError}</div>
            )}
            {!loading && !loadError && filteredStaff.length === 0 && (
              <div className="p-6 text-center text-sm text-gray-500">
                No staff found. Use "{t('add_staff')}" to create the first record.
              </div>
            )}
            {!loading && filteredStaff.map(staffMember => (
              <div
                key={staffMember.id}
                onClick={() => setSelectedStaff(staffMember)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedStaff?.id === staffMember.id ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                {/* Staff Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {getStaffTypeIcon(staffMember.staff_type)}
                    <div>
                      <h3 className="font-semibold text-gray-900">{staffMember.full_name}</h3>
                      <p className="text-sm text-gray-600">{staffMember.position}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(staffMember.employment_status)}`}>
                      {staffMember.employment_status}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      staffMember.staff_type === 'EXTERNAL_PROVIDER' ? 'text-indigo-600 bg-indigo-100' : 'text-blue-600 bg-blue-100'
                    }`}>
                      {staffMember.staff_type === 'EXTERNAL_PROVIDER' ? <><Building size={12} className="inline mr-1" />{t('external')}</> : <><Home size={12} className="inline mr-1" />{t('internal')}</>}
                    </span>
                  </div>
                </div>

                {/* Staff Details */}
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Code:</span>
                    <span>{staffMember.staff_code}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Department:</span>
                    <span>{staffMember.department}</span>
                  </div>
                  {staffMember.staff_type === 'DRIVER' && (
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">License:</span>
                      <span>{staffMember.driver_license_class}</span>
                      <span className="text-xs">
                        (Exp: {staffMember.license_expiry_date?.toLocaleDateString()})
                      </span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Missions:</span>
                    <span>{staffMember.total_missions_completed}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Staff Details */}
        <div className="flex-1 bg-gray-50 overflow-hidden">
          {selectedStaff ? (
            <div className="h-full flex flex-col">
              {/* Staff Profile Header */}
              <div className="bg-white border-b border-gray-200 p-6 flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                      {getStaffTypeIcon(selectedStaff.staff_type)}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedStaff.full_name}</h2>
                      <p className="text-gray-600">{selectedStaff.position} • {selectedStaff.department}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(selectedStaff.employment_status)}`}>
                          {selectedStaff.employment_status}
                        </span>
                        <span className={`px-3 py-1 text-sm rounded-full ${
                          selectedStaff.staff_type === 'EXTERNAL_PROVIDER' ? 'text-indigo-600 bg-indigo-100' : 'text-blue-600 bg-blue-100'
                        }`}>
                          {selectedStaff.staff_type === 'EXTERNAL_PROVIDER' ? <><Building size={14} className="inline mr-1" />{t('external_provider')}</> : <><Home size={14} className="inline mr-1" />{t('internal_staff')}</>}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditStaff(selectedStaff)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>{t('edit')}</span>
                    </button>
                    <button
                      onClick={() => handleDeleteStaff(selectedStaff)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{t('remove')}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Staff Details Content */}
              <div className="flex-1 p-6 overflow-y-auto">
                {activeTab === 'directory' && (
                  <div className="grid grid-cols-2 gap-6">
                    {/* Personal Information */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('personal_info')}</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Staff Code</label>
                          <p className="text-gray-900">{selectedStaff.staff_code}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Email</label>
                          <p className="text-gray-900">{selectedStaff.email || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Phone</label>
                          <p className="text-gray-900">{selectedStaff.phone || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Hire Date</label>
                          <p className="text-gray-900">{selectedStaff.hire_date.toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Professional Information */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('professional_info')}</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Staff Type</label>
                          <div className="flex items-center space-x-2">
                            {getStaffTypeIcon(selectedStaff.staff_type)}
                            <span className="text-gray-900">{selectedStaff.staff_type}</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Department</label>
                          <p className="text-gray-900">{selectedStaff.department}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Supervisor</label>
                          <p className="text-gray-900">{selectedStaff.supervisor_name || 'None assigned'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Shift Pattern</label>
                          <p className="text-gray-900">{selectedStaff.shift_pattern}</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver-Specific Information */}
                    {selectedStaff.staff_type === 'DRIVER' && (
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('driver_info')}</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-500">Numéro de Permis</label>
                            <p className="text-gray-900 font-mono">{selectedStaff.driver_license_number || 'Non renseigné'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Classe de Permis</label>
                            <p className="text-gray-900">{selectedStaff.driver_license_class}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Expiration du Permis</label>
                            <p className="text-gray-900">
                              {selectedStaff.license_expiry_date?.toLocaleDateString()}
                              {selectedStaff.license_expiry_date && selectedStaff.license_expiry_date < new Date(Date.now() + 30*24*60*60*1000) && (
                                <span className="ml-2 text-red-600 text-sm">⚠️ Expire Bientôt</span>
                              )}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Missions Completed</label>
                            <p className="text-gray-900">{selectedStaff.total_missions_completed}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Employment Summary */}
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Résumé d'Emploi</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Type d'Emploi</label>
                          <p className="text-gray-900">
                            {selectedStaff.staff_type === 'EXTERNAL_PROVIDER' ? <><Building size={14} className="inline mr-1" />{t('external_provider')}</> : <><Home size={14} className="inline mr-1" />{t('internal_staff')}</>}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Missions Accomplies</label>
                          <p className="text-gray-900">{selectedStaff.total_missions_completed}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Ancienneté</label>
                          <p className="text-gray-900">
                            {Math.floor((Date.now() - selectedStaff.hire_date.getTime()) / (1000 * 60 * 60 * 24 * 365))} ans
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'availability' && (
                  <StaffAvailability
                    staff={staff}
                    selectedStaff={selectedStaff}
                    showMessage={showMessage}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('select_staff')}</h3>
                <p className="text-gray-600">{t('select_staff_desc')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Staff Form Modal */}
      <StaffForm
        isOpen={showStaffForm}
        onClose={() => {
          setShowStaffForm(false)
          setEditingStaff(null)
        }}
        onSave={handleSaveStaff}
        editingStaff={editingStaff}
        supervisors={staff.filter(s => s.staff_type === 'SUPERVISOR')}
        showMessage={showMessage}
      />
    </div>
  )
}

export default FleetStaffManagement
